export const chunkArray = (arr, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunks.push(arr.slice(i, i + chunkSize));
  }
  return chunks;
};

export const getCommaOut = (value) => {
  if (value) return value.toString().replace(/,/g, "");
  return value;
};

export const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

export const formatDateWithYear = (date, customMonth = "long") => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: customMonth,
    year: "numeric",
  });
};

export const formatBillingCurrency = (cents, currency = "SGD") => {
  if (cents === null || cents === undefined || isNaN(cents)) {
    return new Intl.NumberFormat("en-SG", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(0);
  }
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents.toFixed(2) / 100);
};

export const formatDashedDateWithYear = (date, padMonth = true) => {
  if (!date) return "";

  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(padMonth ? 2 : 1, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

export const formatTextFieldDate = (date, padMonth = true) => {
  if (!date) return "";

  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(padMonth ? 2 : 1, "0");
  const year = d.getFullYear();

  return `${year}-${month}-${day}`;
};

export const formatPdfDate = (date) => {
  if (!date) return "";
  date = new Date(date);
  const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(
    date.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;

  return formattedDate;
};

export function roundToUpperWhole(number, formatIt = false) {
  let rounded = Math.ceil(number / 1000) * 1000;
  if (formatIt) return rounded.toLocaleString("en-US");
  return rounded;
}

export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
};

export const formatDateLocal = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Format a date as YYYY-MM-DD using local date parts (no UTC conversion) to avoid timezone shift when sending to API. */
export const formatDateForAPI = (date) => {
  if (date == null || date === "") return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateYearOnly = (date) => {
  if (!date) return "";
  return new Date(date).getFullYear().toString();
};

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

export const formatAmount = (
  amount,
  needRound = true,
  forceNegative = false,
  convertSign = false,
  showDecimals = false,
) => {
  let numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) {
    return "0";
  }

  if (convertSign) {
    numericAmount = -numericAmount;
  }

  let formattedAmount;

  if (showDecimals) {
    // Show 2 decimal places without rounding
    formattedAmount = Math.abs(numericAmount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else if (needRound) {
    // Round to whole numbers (existing behavior)
    formattedAmount = Math.abs(Math.round(numericAmount)).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    );
  } else {
    // Show 2 decimal places without rounding (original behavior when needRound was false)
    formattedAmount = Math.abs(numericAmount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (numericAmount === 0) return "0";
  return numericAmount < 0 || forceNegative
    ? `(${formattedAmount})`
    : formattedAmount;
};

export const formatScheduleName = (name, no) => {
  return `${name || ""} - ${no || ""}`;
};

export const formatInputAmount = (amount, needRound = true) => {
  // Extract numbers and operators, preserving the structure
  const parts = amount.toString().match(/[-+]?\d+(\,\d{3})*(\.\d+)?|[-+]/g);

  if (!parts) {
    return "0";
  }

  const formattedParts = parts.map((part) => {
    if (/[-+]/.test(part)) {
      // Return operators as they are
      return part;
    } else {
      // Handle numeric parts
      const numericPart = parseFloat(part.replace(/,/g, "")); // Remove commas for parsing
      if (isNaN(numericPart)) {
        return "0";
      }

      // Format the number based on the rounding flag
      let formattedNumber = Math.abs(numericPart).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      if (needRound) {
        formattedNumber = Math.abs(Math.round(numericPart)).toLocaleString(
          "en-US",
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          },
        );
      }

      return numericPart < 0 ? `(${formattedNumber})` : formattedNumber;
    }
  });

  // Join the parts back together
  return formattedParts.join("");
};

export const formatHistoryAmount = (historyAmount) => {
  if (!Array.isArray(historyAmount) || historyAmount.length === 0)
    return historyAmount;

  const amounts = historyAmount.map(Number);

  const formattedString = amounts
    .map((amount, index) =>
      index === 0 ? amount : amount >= 0 ? `+${amount}` : amount,
    )
    .join("");

  const result = amounts.reduce((acc, num) => acc + num, 0);

  return `${formattedString} = ${result}`;
};

export const formatPdfAmountVisual = (amount, signMode, needRound = true) => {
  if (signMode === "negative")
    return "(" + formatAmount(amount, needRound) + ")";
  else if (signMode === "dynamic") {
    return formatAmount(amount, needRound);
  } else return formatAmount(amount, needRound);
};

export const cleanUpAmount = (amount) => {
  return amount.replace(/[^0-9+\-*/.]/g, "");
};

export const currencyRateConversion = (amount, rate, currency) => {
  if (currency === "SGD") {
    return amount * rate;
  } else {
    return amount / rate;
  }
};

export const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

export const checkFlipValue = (flip) => {
  return flip === "negative";
};

export const serializeData = (data) => {
  const compactData = {};
  for (const [rowKey, rowValue] of Object.entries(data)) {
    const filteredRow = {};
    for (const [colKey, colValue] of Object.entries(rowValue)) {
      if (colValue !== null && colValue !== undefined && colValue !== "") {
        filteredRow[colKey] = colValue;
      }
    }
    if (Object.keys(filteredRow).length > 0) {
      compactData[rowKey] = filteredRow;
    }
  }
  return JSON.stringify(compactData);
};

export const deserializeData = (dataString) => {
  const parsedData = JSON.parse(dataString);
  return new Map(Object.entries(parsedData));
};

export const transformMapToArray = (mapData) => {
  const rows = [];
  mapData.forEach((value, key) => {
    const rowIndex = parseInt(key.replace(/\D/g, "")) - 1; // Extract row index (0-based)
    const colIndex = key.replace(/[0-9]/g, "").charCodeAt(0) - 65; // Extract column index (0-based)

    if (!rows[rowIndex]) rows[rowIndex] = [];
    rows[rowIndex][colIndex] = value; // Save cell object directly
  });

  // Normalize rows to ensure all rows have the same number of columns
  const maxCols = Math.max(...rows.map((r) => (r ? r.length : 0)));
  return rows.map((row) =>
    row
      ? Array.from({ length: maxCols }, (_, i) => row[i] || null)
      : Array(maxCols).fill(null),
  );
};

// Convert Map to Array of Objects
export const mapToArray = (mapData) => {
  const rows = [];
  const headers = new Set();

  Array.from(mapData.entries()).forEach(([key, value]) => {
    headers.add(key);
    rows.push(value);
  });

  return {
    headers: Array.from(headers),
    rows,
  };
};

export const handleCurrencyConversion = (data, to, rate, setViewCallback) => {
  setViewCallback(to); // Update the currency view state

  for (let key in data) {
    if (Array.isArray(data[key])) {
      data[key].forEach((item) => {
        item.amount = currencyRateConversion(item.amount, rate, to);

        // Handle nested history amounts if they exist
        if (item.history && Array.isArray(item.history)) {
          item.history.forEach((historyItem) => {
            if (historyItem.amount && Array.isArray(historyItem.amount)) {
              historyItem.amount.forEach((value, index) => {
                historyItem.amount[index] = currencyRateConversion(
                  value,
                  rate,
                  to,
                );
              });
            }
          });
        }

        // Handle historyAmount if it exists
        if (item.historyAmount && Array.isArray(item.historyAmount)) {
          item.historyAmount.forEach((value, index) => {
            item.historyAmount[index] = currencyRateConversion(value, rate, to);
          });
        }
      });
    }
  }
};

export const findOrCreateEntry = (map, key, createFunc) => {
  if (!map.has(key)) map.set(key, createFunc());
  return map.get(key);
};

export const expensesAdjustment = (
  adjustment,
  amount,
  hasVacantPeriod,
  vacantPeriods,
  rentalPeriods,
) => {
  const DIRECT = "Direct";
  const APPORTION = "Apportion";
  const NONDEDUCTIBLE = "Non-deductible";

  // Sum total days
  const totalVacantDays = vacantPeriods?.reduce(
    (sum, p) => sum + (p.days || 0),
    0,
  );
  const totalRentalDays = rentalPeriods?.reduce(
    (sum, p) => sum + (p.days || 0),
    0,
  );

  let portion = 1;
  if (hasVacantPeriod && totalRentalDays > 0) {
    portion = totalVacantDays / totalRentalDays;
  }

  switch (adjustment) {
    case DIRECT:
      return 0;
    case NONDEDUCTIBLE:
      return amount;
    case APPORTION:
      return amount * Math.abs(portion);
    default:
      return amount;
  }
};

export const isAutoload = (status) => {
  return status === "autoload";
};

export function capitalizeFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export const areAssetsEqual = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export function sumObjects(o1, o2) {
  const result = {};
  const keys = new Set([...Object.keys(o1), ...Object.keys(o2)]);

  keys.forEach((key) => {
    const v1 = o1[key] ?? 0;
    const v2 = o2[key] ?? 0;

    if (
      typeof v1 === "object" &&
      typeof v2 === "object" &&
      v1 !== null &&
      v2 !== null
    ) {
      // recursively sum nested objects
      result[key] = sumObjects(v1, v2);
    } else if (typeof v1 === "number" && typeof v2 === "number") {
      result[key] = v1 + v2;
    } else {
      result[key] = v1 || v2; // fallback if not a number
    }
  });

  return result;
}
